import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useCoffeeWhitePages } from '../../shell/useCoffeeWhitePages';
import { styleSheet } from './styles';

interface IProps {
	/** `light` only: sits above the title, e.g. a back link to the parent view. */
	aboveTitle?: React.ReactNode;
	/**
	 * `light` sets the page in a white card on the Coffee blue (or a full-white page, see `useCoffeeWhitePages`) and heads it
	 * like the rebuilt Coffee pages (Call log): a left-aligned title with the page's actions on the same row. `coffee` keeps
	 * the blue page and the centered display title.
	 */
	appearance?: 'coffee' | 'light';
	bodyStyles?: StyleDeclarationValue[];
	className?: string;
	/** Controls on the title row: right of the title on `light`, pinned right of the centered title on `coffee`. */
	headerActions?: React.ReactNode;
	headerStyles?: StyleDeclarationValue[];
	/**
	 * `light` only: the page fills the scrolling body's height and the body becomes a flex column, so a list inside can
	 * scroll on its own under a pinned title and filters. Below `md` the page grows with its content instead.
	 */
	fillHeight?: boolean;
	/** `light` only: overrides on the page box, e.g. a page that uses the full width instead of the left column. */
	pageStyles?: StyleDeclarationValue[];
	/** `light` only: a line under the title. */
	subtitle?: React.ReactNode;
	title?: React.ReactNode;
	/** `light` only: sits beside the title but outside the heading, e.g. an info button. */
	titleAddon?: React.ReactNode;
}

export const BasicPage: React.FC<React.PropsWithChildren<IProps>> = ({
	aboveTitle,
	appearance = 'coffee',
	children,
	bodyStyles = [],
	className = '',
	headerActions,
	fillHeight = false,
	headerStyles = [],
	pageStyles = [],
	subtitle,
	title,
	titleAddon,
}) => {
	const [whitePages] = useCoffeeWhitePages();
	if (appearance === 'light') {
		const page = (
			<div
				className={`${css(
					styleSheet.lightPage,
					whitePages && styleSheet.lightPageWhite,
					fillHeight && styleSheet.lightPageFill,
					...pageStyles
				)} ${className}`}
			>
				<div
					className={css(
						styleSheet.lightCard,
						whitePages && styleSheet.lightCardFlat,
						fillHeight && styleSheet.lightCardFill
					)}
				>
					<div className={css(styleSheet.lightHeader, ...headerStyles)}>
						<div className={css(styleSheet.lightHeading)}>
							{aboveTitle}
							<div className={css(styleSheet.lightTitleRow)}>
								<h1 className={css(styleSheet.lightTitle)}>{title}</h1>
								{titleAddon}
							</div>
							{subtitle ? <p className={css(styleSheet.lightSubtitle)}>{subtitle}</p> : null}
						</div>
						{headerActions ? <div className={css(styleSheet.lightHeaderActions)}>{headerActions}</div> : null}
					</div>
					<div className={css(styleSheet.body, fillHeight && styleSheet.lightBodyFill, ...bodyStyles)}>{children}</div>
				</div>
			</div>
		);
		return whitePages ? (
			<div className={css(styleSheet.lightBackdrop, fillHeight && styleSheet.lightBackdropFill)}>{page}</div>
		) : (
			page
		);
	}
	return (
		<div className={`${css(styleSheet.basicPageContainer)} ${className}`}>
			<div className={css(styleSheet.header, ...headerStyles)}>
				<div className={css(styleSheet.title)}>{title}</div>
				{headerActions ? <div className={css(styleSheet.headerActions)}>{headerActions}</div> : null}
			</div>
			<div className={css(styleSheet.body, ...bodyStyles)}>{children}</div>
		</div>
	);
};
